/**
 * Badge color definitions for Excel export cell styling.
 *
 * Format: { columnHeader: [ { match, bg, fg }, ... ] }
 * - match: case-insensitive substring match against cell value
 * - bg:    hex fill color (no #)
 * - fg:    hex font color (no #)
 *
 * The backend applies the first matching rule per cell.
 */

import { RISK_SUMMARY_COLUMN_ENABLED } from "./myVendorsConstants";

// ── Palette (subtle pastel fills matching the UI badge colors) ───────────────
const GREEN   = { bg: 'DCFCE7', fg: '166534' };
const YELLOW  = { bg: 'FEF3C7', fg: '92400E' };
const RED     = { bg: 'FEE2E2', fg: '991B1B' };
const BLUE    = { bg: 'DBEAFE', fg: '1E40AF' };
const PURPLE  = { bg: 'F3E8FF', fg: '7E22CE' };
const INDIGO  = { bg: 'E0E7FF', fg: '4338CA' };
const GRAY    = { bg: 'F3F4F6', fg: '374151' };
const TEAL    = { bg: 'CCFBF1', fg: '0F766E' };
const EMERALD = { bg: 'D1FAE5', fg: '065F46' };
const ORANGE  = { bg: 'FFEDD5', fg: '9A3412' };

// ── Status column rules (shared across RFI, Assessment, Documents) ───────────
const STATUS_RULES = [
  { match: 'Complete',    ...GREEN },
  { match: 'Released',    ...GREEN },
  { match: 'Published',   ...GREEN },
  { match: 'Active',      ...GREEN },
  { match: 'Visible',     ...GREEN },
  { match: 'In Progress', ...YELLOW },
  { match: 'Pending',     ...YELLOW },
  { match: 'In Review',   ...YELLOW },
  { match: 'Not Started', ...GRAY },
  { match: 'Upcoming',    ...BLUE },
  { match: 'Scheduled',   ...BLUE },
  { match: 'Inactive',    ...RED },
  { match: 'Cancelled',   ...RED },
  { match: 'Expired',     ...RED },
  { match: 'Overdue',     ...RED },
  { match: 'Archived',    ...GRAY },
  { match: 'Hidden',      ...GRAY },
  { match: 'Draft',       ...GRAY },
];

// ── Risk level rules ─────────────────────────────────────────────────────────
const RISK_RULES = [
  { match: 'Low',      ...GREEN },
  { match: 'Medium',   ...YELLOW },
  { match: 'High',     ...ORANGE },
  { match: 'Critical', ...RED },
];

// ── GxP rules ────────────────────────────────────────────────────────────────
const GXP_RULES = [
  { match: 'GCP', ...BLUE },
  { match: 'GMP', ...GREEN },
  { match: 'GLP', ...PURPLE },
  { match: 'GVP', ...INDIGO },
  { match: 'GDP', ...TEAL },
  { match: 'GXP', ...GRAY },
];

// ── Vendor type rules ────────────────────────────────────────────────────────
const VENDOR_TYPE_RULES = [
  { match: 'CRO',  ...BLUE },
  { match: 'CDMO', ...PURPLE },
  { match: 'Lab',  ...EMERALD },
  { match: 'CMO',  ...INDIGO },
];

// ── Portal access rules ──────────────────────────────────────────────────────
const PORTAL_RULES = [
  { match: '360 & Respond', ...INDIGO },
  { match: 'Respond & 360', ...INDIGO },
  { match: '360',           ...BLUE },
  { match: 'Respond',       ...TEAL },
];

// ── Role rules ───────────────────────────────────────────────────────────────
const ROLE_RULES = [
  { match: 'Admin',       ...PURPLE },
  { match: 'Manager',     ...BLUE },
  { match: 'User',        ...GRAY },
  { match: 'VQQ Manager', ...INDIGO },
  { match: 'RFI Manager', ...INDIGO },
  { match: 'CRO User',    ...TEAL },
];


// ── Pre-built cell style maps for each screen ────────────────────────────────

export const rfiCellStyles = {
  'Status':       STATUS_RULES,
  'GxP':          GXP_RULES,
  'Risk Summary': RISK_RULES,
  'Type':         [
    { match: 'Diligent VQQ', ...INDIGO },
    { match: 'Diligent RFI', ...INDIGO },
    { match: 'Custom VQQ',   ...GRAY },
    { match: 'Custom RFI',   ...GRAY },
  ],
};

export const assessmentCellStyles = {
  'Status': STATUS_RULES,
  'GxP':    GXP_RULES,
};

export const documentsCellStyles = {
  'Status':       STATUS_RULES,
  'GxP':          GXP_RULES,
  'Risk Summary': RISK_RULES,
  'Type':         [
    { match: 'VQQ',   ...BLUE },
    { match: 'RFI',   ...BLUE },
    { match: 'Audit', ...PURPLE },
  ],
};

export const documentLibraryCellStyles = {
  'Status':           STATUS_RULES,
  'Document Type':    [
    { match: 'VQQ',   ...BLUE },
    { match: 'RFI',   ...BLUE },
    { match: 'Audit', ...PURPLE },
  ],
  'GxP':              GXP_RULES,
  'Onsite / Remote':  [
    { match: 'Onsite', ...BLUE },
    { match: 'Remote', ...TEAL },
  ],
};

export const vendorDiscoveryCellStyles = {
  'GxP':  GXP_RULES,
  'Type': VENDOR_TYPE_RULES,
};

const MY_VENDORS_RISK_SUMMARY_STYLES = [
  { match: 'Green',  ...GREEN },
  { match: 'Yellow', ...YELLOW },
  { match: 'Red',    ...RED },
];

export const myVendorsCellStyles = {
  ...(RISK_SUMMARY_COLUMN_ENABLED ? { 'Risk Summary': MY_VENDORS_RISK_SUMMARY_STYLES } : {}),
  'GxP Profile': GXP_RULES,
  'Type': [
    ...VENDOR_TYPE_RULES,
    { match: 'Service/Technology Provider', ...TEAL },
    { match: 'Technology/Service Provider', ...TEAL },
  ],
};

export const myVendorsPageCellStyles = {
  ...(RISK_SUMMARY_COLUMN_ENABLED ? { 'Risk Summary': MY_VENDORS_RISK_SUMMARY_STYLES } : {}),
  'Qualified GxPs': GXP_RULES,
  'Type': myVendorsCellStyles['Type'],
};

export const sponsorOrgCellStyles = {
  'Status': STATUS_RULES,
};

export const vendorOrgCellStyles = {
  'Active Status':    STATUS_RULES,
  'Discovery Status': STATUS_RULES,
  'Type':             VENDOR_TYPE_RULES,
  'Portal Access':    PORTAL_RULES,
};

export const usersCellStyles = {
  'Role':          ROLE_RULES,
  'Portal Access': PORTAL_RULES,
  'Last Active':   [
    { match: 'Active', ...GREEN },
  ],
};

export const sponsorBannerOrgCellStyles = sponsorOrgCellStyles;
export const sponsorBannerUsersCellStyles = usersCellStyles;
export const vendorBannerOrgCellStyles = vendorOrgCellStyles;
export const vendorBannerUsersCellStyles = usersCellStyles;
